import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CardPayment } from '@lib/entities';
import { CardOrOneTimePayment, GiftCard } from '@lib/entities/Payment';
import { AddPaymentV4Payload } from '@lib/api/CheckoutService';

export interface WalletState {
    loading: boolean;
    cards: CardPayment[];
    selectedCard?: CardOrOneTimePayment;
    giftCards?: GiftCard[];
    jsCreditBalance: number;
    error: string | null;
    gatewayLegend?: string;
    /** True when wallet-info returns a 428 requiring device verification. */
    requireDeviceVerification?: boolean;
    /**
     * True (from wallet-info, v4+) when the user has a saved card stranded on the
     * inactive gateway with no equivalent on the active one. Drives the differentiated
     * "re-add your card" empty state (JSAD-6863).
     */
    savedCardNeedsReentry?: boolean;
}

const initialState: WalletState = {
    loading: false,
    cards: [],
    selectedCard: undefined,
    jsCreditBalance: 0,
    error: null,
    requireDeviceVerification: false,
    savedCardNeedsReentry: false
};

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCards: (state, action: PayloadAction<CardPayment[]>) => {
            state.cards = action.payload;
        },
        setSelectedCard: (state, action: PayloadAction<CardOrOneTimePayment | undefined>) => {
            state.selectedCard = action.payload;
        },
        setGiftCards: (state, action: PayloadAction<GiftCard[]>) => {
            state.giftCards = action.payload;
        },
        setJsCreditBalance: (state, action: PayloadAction<number>) => {
            state.jsCreditBalance = action.payload;
        },
        setWalletError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setGatewayLegend: (state, action: PayloadAction<string | undefined>) => {
            state.gatewayLegend = action.payload;
        },
        setRequireDeviceVerification: (state, action: PayloadAction<boolean>) => {
            state.requireDeviceVerification = action.payload;
        },
        setSavedCardNeedsReentry: (state, action: PayloadAction<boolean>) => {
            state.savedCardNeedsReentry = action.payload;
        },
        clearSelectedCard: (state) => {
            state.selectedCard = undefined;
        },
        resetWallet: (state) => {
            return initialState;
        },
        // Actions to trigger sagas
        fetchWalletRequest: (
            state,
            action: PayloadAction<{
                userId?: number;
                selectFirstCard?: boolean;
                storeId?: number;
                isMfaEnabled?: boolean;
            }>
        ) => {
            state.loading = true;
            state.error = null;
        },
        addCardRequest: (
            state,
            action: PayloadAction<{
                newCard: AddPaymentV4Payload;
                cc_last4: string;
                userId: number;
                storeId?: number;
            }>
        ) => {
            state.loading = true;
        },
        removeCardRequest: (
            state,
            action: PayloadAction<{ card: CardPayment; userId: number; storeId?: number }>
        ) => {
            state.loading = true;
        },
        removeSelectedCardRequest: (
            state,
            action: PayloadAction<{ userId: number; storeId?: number }>
        ) => {
            state.loading = true;
        }
    }
});

export const {
    setLoading,
    setCards,
    setSelectedCard,
    setGiftCards,
    setJsCreditBalance,
    setWalletError,
    setGatewayLegend,
    setRequireDeviceVerification,
    setSavedCardNeedsReentry,
    clearSelectedCard,
    resetWallet,
    fetchWalletRequest,
    addCardRequest,
    removeCardRequest,
    removeSelectedCardRequest
} = walletSlice.actions;

export const selectWallet = (state: { wallet: WalletState }) => state.wallet;
export const selectSelectedCard = (state: { wallet: WalletState }) => state.wallet.selectedCard;
export const selectGatewayLegend = (state: { wallet: WalletState }) => state.wallet.gatewayLegend;
export const selectSavedCardNeedsReentry = (state: { wallet: WalletState }) =>
    state.wallet.savedCardNeedsReentry;

export default walletSlice.reducer;
